import type { Ring, Vec2 } from '../../geo/types';
import { pointInRing } from '../../geo/polygon';

/** A yard the woodcut shows open inside a block, and where it was traced on the woodcut, in its pixels. */
export interface WoodcutYard {
  readonly id: string;
  readonly ring: Ring;
  readonly woodcut: {
    readonly x0: number;
    readonly y0: number;
    readonly x1: number;
    readonly y1: number;
  };
}

/**
 * PLAN Lots: the yards Cornelis Anthonisz's woodcut of 1544 shows open inside the blocks: in two
 * blocks by the IJ and the Geldersekade, racks on posts, stacks and a tree between the buildings; in
 * the three blocks between the Lange Niezel and the Sint Olofssteeg, trees among the roofs, in the
 * middle of the blocks between the Warmoesstraat and the Oudezijds Voorburgwal. There no building is
 * shortened to fit its plot or widened, and the ground no plot reaches stays open. Traced on
 * docs/reference/woodcut-1544-uva-8000px.jpg (crops in docs/reference/crops/woodcut-yards), carried
 * to layout metres by the woodcut's georeference (tools/layout/woodcut_georef.py; 61 control points
 * and a leave-one-out error of 21 m on 2026-09-27, so the traced outline places a yard within its
 * block, not a building) and cut to the block each lies in.
 */
export const WOODCUT_YARDS: readonly WoodcutYard[] = [
  {
    id: 'kolk-ij-triangle',
    ring: [
      [529.7, 341.9],
      [533.5, 351.1],
      [566.9, 371.9],
      [582.4, 378.2],
      [586.9, 362.1],
      [575.2, 343.8],
      [540.1, 313.5],
      [535.8, 319.5],
    ],
    woodcut: { x0: 1790, y0: 3042, x1: 1921, y1: 3200 },
  },
  {
    id: 'geldersekade-tower',
    ring: [
      [570.3, 297.9],
      [617.1, 348.6],
      [624.5, 334.4],
      [609.5, 308.2],
      [601.1, 287.5],
      [593.1, 294.1],
      [576.8, 284.2],
    ],
    woodcut: { x0: 1580, y0: 3100, x1: 1687, y1: 3260 },
  },
  {
    id: 'lange-niezel-heintje-hoek',
    ring: [
      [424.9, 222.4],
      [438.9, 234.0],
      [446.4, 229.8],
      [448.6, 226.6],
      [431.0, 211.3],
    ],
    woodcut: { x0: 1772, y0: 2666, x1: 1813, y1: 2716 },
  },
  {
    id: 'heintje-hoek-armsteeg',
    ring: [
      [425.6, 254.7],
      [449.5, 272.0],
      [456.0, 259.3],
      [432.7, 242.4],
    ],
    woodcut: { x0: 1845, y0: 2692, x1: 1893, y1: 2768 },
  },
  {
    id: 'armsteeg-olofssteeg',
    ring: [
      [457.7, 272.1],
      [463.8, 277.1],
      [472.2, 271.8],
      [473.1, 269.8],
      [462.9, 261.4],
    ],
    woodcut: { x0: 1838, y0: 2785, x1: 1881, y1: 2825 },
  },
];

export function inWoodcutYard(p: Vec2): boolean {
  return WOODCUT_YARDS.some((yard) => pointInRing(p, yard.ring));
}
